/**
 * Renderers del módulo radio.
 *
 * Cada función recibe el estado y rellena una zona del DOM concreta del modal.
 * Los renderers SON puros respecto al DOM que NO sea el suyo: no tocan
 * audio, no abren modales, no navegan. Solo pintan.
 *
 * Las interacciones (click → reproducir, click × → borrar) se conectan vía
 * callbacks opcionales pasados en el segundo argumento. Si un callback no
 * se proporciona, el item es visualmente idéntico pero inerte. Esto permite
 * que en Fase 4b-2 los renderers funcionen para verificación visual sin
 * que aún exista la lógica de reproducción.
 *
 * Migrado desde renderPlaylists/renderFileList/renderFolderList/renderCover/
 * renderStack del legacy. Comportamiento equivalente.
 */

import { AVAILABLE_COVERS } from './data.js';
import { displayName, escapeHTML, getCover } from './helpers.js';
import { getPlaylist, getTrack, state } from './state.js';
import type { CoverMeta, Playlist } from './types.js';

// ─── Tipos de los callbacks ──────────────────────────────────────────────

export interface RenderCallbacks {
  /** Click en una playlist → activarla. */
  onSelectPlaylist?: (playlistId: string) => void;
  /** Click en × de una playlist → eliminarla. Solo se llama si no está locked. */
  onDeletePlaylist?: (playlistId: string) => void;
  /**
   * Click simple en una pista → SELECCIONARLA sin reproducir. Marca la
   * pista como `currentTrackId` y la carga en el `<audio>` pero deja
   * al usuario pulsar PLAY (o doble click) para empezar.
   */
  onSelectTrack?: (trackId: string) => void;
  /**
   * Doble click en una pista → seleccionar y REPRODUCIR. Atajo para
   * "select + play" sin tener que ir al botón.
   */
  onPlayTrack?: (trackId: string) => void;
  /** Click en − junto a una pista → quitarla de la playlist activa. */
  onRemoveTrackFromPlaylist?: (trackId: string) => void;
  /** Click en + / ✓ junto a un archivo de la carpeta → toggle en la playlist activa. */
  onToggleTrackInPlaylist?: (trackId: string) => void;
}

// ─── Helpers internos ────────────────────────────────────────────────────

/**
 * Devuelve el `name` legible de una pista, sea real o ghost. Si es ghost y
 * tenemos meta con nombre real, lo usamos; si no, devolvemos el id como
 * fallback (caso edge donde la meta no existe).
 */
function trackNameFor(playlist: Playlist, trackId: string): string {
  // Real track (servidor o usuario)
  const real = getTrack(state, trackId);
  if (real) return real.name;
  // Ghost: buscar en userTrackMeta
  const meta = playlist.userTrackMeta?.find((m) => m.id === trackId);
  return meta?.name ?? trackId;
}

// ─── renderPlaylists ─────────────────────────────────────────────────────

/**
 * Pinta la lista de playlists en `#r-pl-list` y enlaza los listeners de
 * click (seleccionar / borrar). También dispara `renderCover()` (porque
 * la cover muestra la playlist activa, dependencia natural).
 */
export function renderPlaylists(cb: RenderCallbacks = {}): void {
  const el = document.getElementById('r-pl-list');
  if (!el) return;

  el.innerHTML = state.playlists
    .map((p) => {
      const count = (p.tracks ?? []).length;
      const isActive = p.id === state.activePlaylistId;
      return `
      <div class="r-pl-item${isActive ? ' r-active' : ''}" data-pid="${escapeHTML(p.id)}">
        <span class="r-pl-icon">📼</span>
        <span class="r-pl-name">${escapeHTML(p.name)}</span>
        <span class="r-pl-count">${count}</span>
        <button class="r-pl-del${p.locked ? ' r-locked' : ''}"
                data-del="${escapeHTML(p.id)}" title="Eliminar playlist">×</button>
      </div>`;
    })
    .join('');

  el.querySelectorAll<HTMLElement>('.r-pl-item').forEach((it) => {
    it.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (target.closest('.r-pl-del')) return;
      const pid = it.dataset.pid;
      if (pid && cb.onSelectPlaylist) cb.onSelectPlaylist(pid);
    });
  });

  el.querySelectorAll<HTMLButtonElement>('.r-pl-del:not(.r-locked)').forEach((b) => {
    b.addEventListener('click', (e) => {
      e.stopPropagation();
      const pid = b.dataset.del;
      if (pid && cb.onDeletePlaylist) cb.onDeletePlaylist(pid);
    });
  });

  // La cover grande depende de la playlist activa: re-render coherente.
  renderCover(cb);
}

// ─── renderFileList ──────────────────────────────────────────────────────

/**
 * Pinta las pistas de la playlist activa en `#r-fx-list`. Cada pista puede
 * ser "real" (con blob URL viva) o "ghost" (referenciada pero sin URL en
 * esta sesión, hasta que el usuario re-elija la carpeta).
 */
export function renderFileList(cb: RenderCallbacks = {}): void {
  const el = document.getElementById('r-fx-list');
  if (!el) return;

  const pl = getPlaylist(state, state.activePlaylistId);
  if (!pl) {
    el.innerHTML = '';
    return;
  }

  // La default playlist es server-backed (locked): su contenido lo gestiona
  // el servidor, así que sus pistas no se quitan a mano desde la UI.
  const locked = Boolean(pl.locked);

  type Item = { type: 'real' | 'ghost'; id: string; name: string };
  const items: Item[] = (pl.tracks ?? []).map((tid) => {
    const real = getTrack(state, tid);
    if (real) return { type: 'real', id: tid, name: real.name };
    return { type: 'ghost', id: tid, name: trackNameFor(pl, tid) };
  });

  if (items.length === 0) {
    el.innerHTML = locked
      ? `<div class="r-fx-empty">Sin música en el servidor todavía.</div>`
      : `<div class="r-fx-empty">
      Playlist vacía.<br>Añade canciones desde <b>CARPETA ↓</b>
    </div>`;
    return;
  }

  el.innerHTML = items
    .map((it) => {
      const isCur = it.id === state.currentTrackId;
      const ghost = it.type === 'ghost';
      const arrow = isCur && !ghost ? '<span class="r-arr">▶</span>' : '<span class="r-ph"></span>';
      const ghostTag = ghost ? ' <em class="r-ghost-tag">(re-añade carpeta)</em>' : '';
      const rmBtn = locked
        ? ''
        : `<button class="r-add-pl r-in" data-rm="${escapeHTML(it.id)}"
                title="Quitar de la playlist">−</button>`;
      return `
      <div class="r-fx-file${isCur ? ' r-cur' : ''}${ghost ? ' r-ghost' : ''}"
           data-id="${escapeHTML(it.id)}"${ghost ? ' data-ghost="1"' : ''}>
        ${arrow}
        <span class="r-note">♪</span>
        <span class="r-name">${escapeHTML(displayName(it.name))}${ghostTag}</span>
        ${rmBtn}
      </div>`;
    })
    .join('');

  el.querySelectorAll<HTMLElement>('.r-fx-file:not(.r-ghost)').forEach((f) => {
    f.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (target.closest('.r-add-pl')) return;
      const id = f.dataset.id;
      if (id && cb.onSelectTrack) cb.onSelectTrack(id);
    });
    f.addEventListener('dblclick', (e) => {
      const target = e.target as HTMLElement;
      if (target.closest('.r-add-pl')) return;
      const id = f.dataset.id;
      if (id && cb.onPlayTrack) cb.onPlayTrack(id);
    });
  });

  el.querySelectorAll<HTMLButtonElement>('.r-add-pl[data-rm]').forEach((b) => {
    b.addEventListener('click', (e) => {
      e.stopPropagation();
      const id = b.dataset.rm;
      if (id && cb.onRemoveTrackFromPlaylist) cb.onRemoveTrackFromPlaylist(id);
    });
  });
}

// ─── renderFolderList ────────────────────────────────────────────────────

/**
 * Pinta los archivos de la carpeta del usuario en `#r-fd-list`. Cada
 * archivo muestra `+` o `✓` según si está ya en la playlist activa.
 */
export function renderFolderList(cb: RenderCallbacks = {}): void {
  const el = document.getElementById('r-fd-list');
  if (!el) return;

  const pl = getPlaylist(state, state.activePlaylistId);
  // Si la playlist activa es la del escuadrón (locked), no se le pueden
  // añadir tracks de carpeta: el botón "+" se oculta. Las tracks de
  // carpeta se añaden a playlists del usuario ("+ NUEVA").
  const locked = Boolean(pl?.locked);
  const inPl = (id: string): boolean => Boolean(pl && (pl.tracks ?? []).includes(id));

  if (state.userTracks.length === 0) {
    el.innerHTML = `<div class="r-fx-empty">
      Sin carpeta seleccionada.<br>Pulsa <b>+ CARPETA</b> para elegir una.
    </div>`;
    return;
  }

  el.innerHTML = state.userTracks
    .map((t) => {
      const inside = inPl(t.id);
      const titleAct = inside ? 'Quitar de' : 'Añadir a';
      const sym = inside ? '✓' : '+';
      const addBtn = locked
        ? ''
        : `<button class="r-add-pl${inside ? ' r-in' : ''}" data-add="${escapeHTML(t.id)}"
                title="${titleAct} playlist activa">${sym}</button>`;
      return `
      <div class="r-fx-file" data-id="${escapeHTML(t.id)}">
        <span class="r-ph"></span>
        <span class="r-note">♪</span>
        <span class="r-name">${escapeHTML(displayName(t.name))}</span>
        ${addBtn}
      </div>`;
    })
    .join('');

  el.querySelectorAll<HTMLButtonElement>('.r-add-pl[data-add]').forEach((b) => {
    b.addEventListener('click', (e) => {
      e.stopPropagation();
      const id = b.dataset.add;
      if (id && cb.onToggleTrackInPlaylist) cb.onToggleTrackInPlaylist(id);
    });
  });

  // Click simple selecciona; doble click reproduce.
  el.querySelectorAll<HTMLElement>('.r-fx-file').forEach((f) => {
    f.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (target.closest('.r-add-pl')) return;
      const id = f.dataset.id;
      if (id && cb.onSelectTrack) cb.onSelectTrack(id);
    });
    f.addEventListener('dblclick', (e) => {
      const target = e.target as HTMLElement;
      if (target.closest('.r-add-pl')) return;
      const id = f.dataset.id;
      if (id && cb.onPlayTrack) cb.onPlayTrack(id);
    });
  });
}

// ─── renderCover ─────────────────────────────────────────────────────────

/**
 * Pinta la cover grande de la playlist activa: la imagen, el texto del
 * lomo (si la cover no tiene `textBaked`), y el tag inferior.
 *
 * Usa la variable CSS `--spine-offset-x` de cada cover si está definida.
 * Esto prepara los retoques de Fase 4c — mientras todas las covers
 * tengan `spineOffsetXPercent: undefined`, el comportamiento es idéntico
 * al legacy (5% por defecto desde el CSS).
 */
export function renderCover(cb: RenderCallbacks = {}): void {
  const pl = getPlaylist(state, state.activePlaylistId);
  const img = document.getElementById('r-cover-img') as HTMLImageElement | null;
  const tag = document.getElementById('r-atag');
  const spine = document.getElementById('r-cover-spine');
  if (!pl || !img) return;

  const cover = getCover(pl.coverId);
  img.src = '/' + cover.file;
  img.alt = pl.name;
  if (tag) tag.textContent = `● ${pl.name} — ${(pl.tracks ?? []).length} TRACKS`;

  if (spine) {
    if (cover.textBaked) {
      spine.style.display = 'none';
    } else {
      spine.style.display = '';
      spine.textContent = pl.name;
      spine.style.color = cover.textColor ?? '#ffffff';
      // Override del offset horizontal si la cover lo define
      if (typeof cover.spineOffsetXPercent === 'number') {
        spine.style.setProperty('--spine-offset-x', `${cover.spineOffsetXPercent}%`);
      } else {
        spine.style.removeProperty('--spine-offset-x');
      }
      // Override de la rotación si la cover lo define (covers con lomo
      // estrecho como cover_7 y cover_8 tienen 89.5°; el resto usa default 90°)
      if (typeof cover.spineRotationDeg === 'number') {
        spine.style.setProperty('--spine-rotation', `${cover.spineRotationDeg}deg`);
      } else {
        spine.style.removeProperty('--spine-rotation');
      }
    }
  }
  renderStack(cb);
}

// ─── renderStack ─────────────────────────────────────────────────────────

/**
 * Pinta la pila de cassettes apilados en `#r-stack`: una cinta por cada
 * playlist inactiva (todas excepto la activa). Click en una activa esa
 * playlist (efecto: insert cartridge).
 */
export function renderStack(cb: RenderCallbacks = {}): void {
  const el = document.getElementById('r-stack');
  if (!el) return;

  const inactive = state.playlists.filter((p) => p.id !== state.activePlaylistId);

  if (inactive.length === 0) {
    el.innerHTML = `<div class="r-stack-empty">
      Sin más playlists.<br>Pulsa <b>+ NUEVA</b> para crear otra.
    </div>`;
    return;
  }

  el.innerHTML = inactive
    .map((p) => {
      const cover = getCover(p.coverId);
      const useFront = Boolean(cover.front);
      const textBaked = Boolean(cover.textBaked);
      const labelColor = cover.textColor ?? '#ffffff';

      // background-image / --cover-bg + --label-color
      const styleParts: string[] = [];
      if (useFront) styleParts.push(`background-image:url('/${cover.front}')`);
      else styleParts.push(`--cover-bg:url('/${cover.file}')`);
      styleParts.push(`--label-color:${labelColor}`);
      const styleAttr = styleParts.join(';');

      const cls = ['r-stack-cassette'];
      if (useFront) cls.push('r-has-front');
      if (textBaked) cls.push('r-text-baked');

      const trackCount = (p.tracks ?? []).length;
      return `
      <div class="${cls.join(' ')}" data-pid="${escapeHTML(p.id)}"
           style="${styleAttr}"
           title="${escapeHTML(p.name)} — ${trackCount} tracks">
        <span class="r-stack-label">${escapeHTML(p.name)}</span>
      </div>`;
    })
    .join('');

  el.querySelectorAll<HTMLElement>('.r-stack-cassette').forEach((c) => {
    c.addEventListener('click', () => {
      const pid = c.dataset.pid;
      if (pid && cb.onSelectPlaylist) cb.onSelectPlaylist(pid);
    });
  });
}

// ─── renderCoverPicker ───────────────────────────────────────────────────

/**
 * Pinta los thumbnails de covers en el modal de "+ NUEVA PLAYLIST".
 * Solo se renderiza una vez al abrir el modal; el toggle de selección
 * se gestiona en Fase 4b-3 con su listener de click.
 *
 * @param selectedCoverId  ID de la cover actualmente seleccionada (resalta).
 * @param onPick           Callback cuando el usuario clica una cover.
 */
export function renderCoverPicker(
  selectedCoverId: string,
  onPick?: (coverId: string) => void,
): void {
  const el = document.getElementById('r-cover-picker');
  if (!el) return;

  el.innerHTML = AVAILABLE_COVERS.map((c: CoverMeta) => {
    const isSel = c.id === selectedCoverId;
    return `
      <div class="r-cover-pick${isSel ? ' r-sel' : ''}" data-cid="${escapeHTML(c.id)}">
        <img src="/${c.file}" alt="${escapeHTML(c.name)}">
        <span>${escapeHTML(c.name)}</span>
      </div>`;
  }).join('');

  el.querySelectorAll<HTMLElement>('.r-cover-pick').forEach((p) => {
    p.addEventListener('click', () => {
      const cid = p.dataset.cid;
      if (cid && onPick) onPick(cid);
    });
  });
}

// ─── renderAll ───────────────────────────────────────────────────────────

/**
 * Re-renderiza todas las zonas dependientes del estado. Útil tras un cambio
 * grande (carga inicial, persistencia recargada, etc.).
 *
 * NOTA: las llamadas individuales a renderCover() y renderStack() son
 * disparadas internamente por renderPlaylists(). No hace falta llamarlas
 * a mano salvo en casos puntuales.
 */
export function renderAll(cb: RenderCallbacks = {}): void {
  renderPlaylists(cb);
  renderFileList(cb);
  renderFolderList(cb);
  // renderCover y renderStack ya las llama renderPlaylists.
}
